import { useTranslation } from "react-i18next";
import { Smartphone, Monitor } from "lucide-react";
import { cn } from "@/lib/utils";
import type { AspectRatio } from "@/lib/api";

const OPTIONS: { value: AspectRatio; label: string; hint: string; Icon: typeof Smartphone }[] = [
  { value: "9:16", label: "format.vertical", hint: "Reels · TikTok · Shorts", Icon: Smartphone },
  { value: "16:9", label: "format.horizontal", hint: "YouTube · TV · Desktop", Icon: Monitor },
];

// Premium segmented control: sliding neon indicator behind the active option.
export function FormatPicker({
  value,
  onChange,
  disabled,
  compact,
}: {
  value: AspectRatio;
  onChange: (v: AspectRatio) => void;
  disabled?: boolean;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const activeIndex = OPTIONS.findIndex((o) => o.value === value);
  return (
    <div
      role="radiogroup"
      data-testid="format-picker"
      className={cn(
        "relative grid grid-cols-2 rounded-xl border border-[#2E7BFF]/25 bg-[#05070D]/80 p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.03),inset_0_0_30px_-20px_rgba(46,123,255,0.6)]",
        disabled && "opacity-60",
      )}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-1 w-[calc(50%-4px)] rounded-lg border border-[#2E7BFF]/60 bg-gradient-to-b from-[#2E7BFF]/25 to-[#0FB6D6]/10 shadow-[0_0_24px_-8px_rgba(46,123,255,0.9),inset_0_1px_0_rgba(255,255,255,0.06)] transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]"
        style={{ transform: `translateX(calc(${activeIndex * 100}% + ${activeIndex * 4}px))`, left: 4 }}
      />
      {OPTIONS.map(({ value: v, label, hint, Icon }) => {
        const active = v === value;
        return (
          <button
            key={v}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            data-testid={`format-option-${v.replace(":", "-")}`}
            onClick={() => onChange(v)}
            className={cn(
              "relative z-10 flex items-center justify-center gap-3 rounded-lg text-left transition-colors duration-200",
              compact ? "px-3 py-2" : "px-4 py-3.5",
              active ? "text-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <span
              className={cn(
                "flex shrink-0 items-center justify-center rounded-[3px] border transition-colors",
                v === "9:16" ? "h-7 w-4" : "h-4 w-7",
                active ? "border-[#7FD4FF] text-[#7FD4FF] shadow-[0_0_10px_-2px_rgba(127,212,255,0.8)]" : "border-current",
              )}
            >
              <Icon className="h-2.5 w-2.5" />
            </span>
            <span className="leading-tight">
              <span className="block font-display text-sm font-bold uppercase tracking-wider">
                {t(label)} <span className={active ? "text-[#7FD4FF]" : ""}>{v}</span>
              </span>
              {!compact && <span className="block text-[11px] text-muted-foreground">{hint}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}
